import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import "@/styles/pitch-black.css";
import App from "@/App";
import { ThemeProvider } from "@/ThemeContext";
import pkg from "../package.json";
import ErrorBoundary from "@/components/ErrorBoundary";
import { installWatchdogClient } from "@/lib/watchdog";

// Send the session cookie on every axios request. Required for the
// deny-by-default AuthGateMiddleware — without this, PWA/SW contexts
// can silently drop cookies on same-origin XHR and every gated
// endpoint returns 401 even though the user is signed in.
// (2026-02-28 — reported symptom: "System Health says sign-in required
// but I am logged in".)
axios.defaults.withCredentials = true;

// Install watchdog error capture as EARLY as possible so we catch
// errors that fire during the very first React render.
installWatchdogClient();

// ---- Lock the app to the screen (user ask 2026-09-13) ----------------
// iOS Safari IGNORES `user-scalable=no` in the viewport meta, so pinch-
// zoom and double-tap zoom still fire. Kill them at the event level so
// the app feels like a native app pinned to the screen. We only block
// MULTI-touch moves (pinch) — single-finger scrolling inside panes is
// untouched, so nothing feels laggy.
(() => {
  const stop = (e) => { e.preventDefault(); };
  document.addEventListener("gesturestart", stop, { passive: false });
  document.addEventListener("gesturechange", stop, { passive: false });
  document.addEventListener("gestureend", stop, { passive: false });
  document.addEventListener(
    "touchmove",
    (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); },
    { passive: false }
  );
  // Block the iOS double-tap-to-zoom without eating normal taps: only
  // cancel a 2nd tap that lands within 300ms of the previous one.
  let lastTouchEnd = 0;
  document.addEventListener(
    "touchend",
    (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    },
    { passive: false }
  );
})();


// Build-version cache buster (2026-05-25). When the running bundle's
// version differs from the one we last saw on this device, wipe every
// sessionStorage entry and the data caches we set in localStorage. This
// kills the "old data flashes for a second then updates" effect that
// users reported after a redeploy — stale rosters / standings /
// highlights would render from sessionStorage before the new fetch
// settled, even though the data was no longer relevant.
//
// Auth-sticky keys we INTENTIONALLY keep across redeploys:
//   - ecDashCrew              : last-known crew payload (instant login feel)
//   - device_id               : push notification identity
//   - theme_*                 : user theme prefs
//   - wow_tutorial_seen_*     : "I've seen the welcome tour" flag — per-crew
//                               (user 2026-06-12: welcome popup was firing on
//                               every login because every redeploy was nuking
//                               this key)
//   - wow_capture_queue_*     : offline email-capture queue (would lose data
//                               on redeploy mid-shift otherwise)
// Everything else gets nuked.
try {
  const seenVersion = localStorage.getItem("appBuildVersion");
  if (seenVersion && seenVersion !== pkg.version) {
    try { sessionStorage.clear(); } catch (_) { /* quota */ }
    // Selective localStorage purge — drop anything that smells like a
    // cached server response (timestamps, list payloads, etc.).
    try {
      const KEEP_PREFIXES = [
        "theme_",
        "device_id",
        "ecDashCrew",
        "appBuildVersion",
        "wow_tutorial_seen_",
        "wow_capture_queue_",
        // Auth/session — MUST survive a redeploy so an app update never
        // forces a re-login (2026-10-02 user report "still seeing a force
        // login after updates"). These were previously nuked on every
        // version bump, which was the #1 cause of forced sign-ins.
        "ecDashUser",
        "ecDashSession",
        "ecDashRefresh",
        "ecDashSoftLock",
      ];
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && !KEEP_PREFIXES.some((p) => k === p || k.startsWith(p))) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => { try { localStorage.removeItem(k); } catch (_) {} });
    } catch (_) { /* ignore */ }
  }
  localStorage.setItem("appBuildVersion", pkg.version);
} catch (_) { /* private mode → soldier on */ }

// ---------------------------------------------------------------------------
// ChunkLoadError auto-recovery. After a redeploy (or a hot rebuild) the cached
// HTML / service worker can reference OLD JS chunk hashes that no longer exist
// → "Loading chunk … failed" / ChunkLoadError, which otherwise crashes the app
// to an error screen. Instead we wipe stale caches + unregister the SW and
// hard-reload ONCE (guarded by a sessionStorage timestamp so a genuinely
// missing chunk can never loop).
// ---------------------------------------------------------------------------
(function installChunkErrorRecovery() {
  const RELOAD_FLAG = "chunkReloadedAt";
  const isChunkError = (v) => {
    const s = typeof v === "string" ? v : "";
    return /Loading chunk [\w-]+ failed/i.test(s)
      || /ChunkLoadError/i.test(s)
      || /Loading CSS chunk/i.test(s)
      || /error loading dynamically imported module/i.test(s);
  };
  // Hard recovery from a stale / mismatched bundle. A deploy (or a dev hot-
  // rebuild) can briefly leave a device running an OLD app shell against NEW
  // code — or vice-versa. That throws either a ChunkLoadError OR a stale
  // ReferenceError like "Can't find variable: HighFiveTile" (a component that
  // only exists in the newer build). We wipe caches + drop the service worker
  // so the next load pulls the CURRENT build. Guarded by a 60s stamp so an
  // AUTOMATIC recovery can never loop; a user-initiated reload passes
  // {force:true} to bypass the guard.
  const forceFreshReload = async ({ force = false } = {}) => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
      if (!force && Date.now() - last < 60000) return;  // auto path → don't loop
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    } catch (_) { /* ignore */ }
    try {
      if (typeof caches !== "undefined" && caches.keys) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch (_) { /* ignore */ }
    try {
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* ignore */ }
    try { window.location.reload(); } catch (_) { window.location.href = "/"; }
  };
  // Exposed so the React ErrorBoundary can self-heal a stale-bundle crash
  // (and so its "Reload" button busts the cache instead of re-serving the
  // same broken code — which is why a crash previously needed a force-quit).
  window.__wowForceFreshReload = forceFreshReload;
  window.addEventListener("error", (e) => {
    if (isChunkError(e && e.message) || isChunkError(e && e.error && e.error.name) || isChunkError(e && e.error && e.error.message)) {
      forceFreshReload();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e && e.reason;
    if (isChunkError(r && r.name) || isChunkError(r && r.message) || isChunkError(String(r))) {
      forceFreshReload();
    }
  });
})();

// Register the service worker (PWA install) AND wire controlled updates
// (2026-10-01). A redeploy ships a new SW that parks in the "waiting" state;
// we surface it to the app which applies it at cold boot (brief "Updating…"
// splash) or via a non-blocking "tap to refresh" pill mid-shift — never a
// forced reload, never a logout (the session in localStorage is untouched).
if ("serviceWorker" in navigator) {
  let refreshing = false;
  // Reload ONLY when we explicitly applied an update — never on the first
  // install's initial claim.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!window.__wowUpdating || refreshing) return;
    refreshing = true;
    try { window.location.reload(); } catch (_) { window.location.href = "/"; }
  });

  // Called by the app (UpdateManager) to swap in the waiting version.
  // Robust: if we don't have a cached waiting-worker reference (it can be
  // missing when the overlay was raised from a stale/duplicate event, or the
  // worker was collected), re-query the registration. If there's STILL no
  // waiting worker to message, just reload — navigation + assets are
  // network-first, so a plain reload pulls the fresh bundle and the new SW
  // activates on its own. The session lives in localStorage and is never
  // touched here, so "you'll stay signed in" always holds.
  window.__wowApplyUpdate = () => {
    window.__wowUpdating = true;
    const w = window.__wowWaitingWorker;
    if (w) {
      try { w.postMessage({ type: "SKIP_WAITING" }); return; } catch (_) { /* fall through */ }
    }
    navigator.serviceWorker.getRegistration()
      .then((reg) => {
        const waiting = reg && reg.waiting;
        if (waiting) { try { waiting.postMessage({ type: "SKIP_WAITING" }); return; } catch (_) {} }
        // Nothing to activate via handshake — hard-reload to the fresh shell.
        try { window.location.reload(); } catch (_) { window.location.href = "/"; }
      })
      .catch(() => { try { window.location.reload(); } catch (_) { window.location.href = "/"; } });
  };

  const notifyWaiting = (worker, atBoot) => {
    window.__wowWaitingWorker = worker;
    try {
      window.dispatchEvent(new CustomEvent("wow:update-waiting", { detail: { atBoot } }));
    } catch (_) { /* ignore */ }
  };

  window.addEventListener("load", () => {
    const bootAt = Date.now();
    const atBoot = () => Date.now() - bootAt < 8000;  // found right after launch → treat as cold-boot update
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // A new SW already waiting from a previous session → apply on this boot.
        if (reg.waiting && navigator.serviceWorker.controller) {
          notifyWaiting(reg.waiting, atBoot());
        }
        // A new SW installing now (redeploy noticed) → surface when ready.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            // 'installed' WITH an existing controller == an update is ready
            // (a first-ever install has no controller → ignore, no reload).
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              notifyWaiting(nw, atBoot());
            }
          });
        });
        // Poll on every cold start so a redeploy is noticed within seconds,
        // not up to 24h.
        try { reg.update(); } catch (_) { /* ignore */ }
        // Light re-check when the app returns to the foreground (store devices
        // stay open for days) so a mid-day push is caught on next focus.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") { try { reg.update(); } catch (_) {} }
        });
      })
      .catch((err) => console.warn("SW registration failed", err));
  });
}

// ─── Build-version watcher (2026-10-04) ────────────────────────────────
// The service-worker update prompt only fires when service-worker.js FILE
// bytes change — but its version string isn't bumped per deploy, so after a
// redeploy the browser saw an identical SW and the "new version ready" pill
// NEVER appeared (user: "not once have I seen the app check for an update").
// The build's /asset-manifest.json DOES change every deploy (new hashed
// bundle filenames), so we poll it and raise the SAME `wow:update-waiting`
// prompt the UpdateManager already renders when the main bundle differs from
// the one we booted with. Tapping Refresh → window.__wowApplyUpdate() →
// network-first reload pulls the fresh build. No manual version bumping,
// works on every deploy automatically.
(function watchBuildVersion() {
  let bootSig = null;
  let announced = false;
  const readSig = async () => {
    try {
      const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return null;
      const j = await r.json();
      const files = j.files || {};
      return files["main.js"] || files["main.css"] ||
        (Array.isArray(j.entrypoints) ? j.entrypoints.join("|") : null);
    } catch (_) { return null; }
  };
  const check = async () => {
    const sig = await readSig();
    if (!sig) return;
    if (bootSig === null) { bootSig = sig; return; }  // prime on first read
    if (sig !== bootSig && !announced) {
      announced = true;
      try {
        window.dispatchEvent(new CustomEvent("wow:update-waiting", { detail: { atBoot: false } }));
      } catch (_) { /* ignore */ }
    }
  };
  check();
  setInterval(check, 60000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
})();


const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
